import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PageBanner from "../PageBanner";
import SocialMediaSidebar from "../SocialMediaSidebar";
import SectionHeader from "../SectionHeader";
import { CMSAPI } from "../../config";
import ScrollToTopButton from "../ScrollToTopButton";

import Policy from "../../assets/images/blog3.png";
import SectionHeaderNoPDF from "../SectionHeaderNoPDF";

const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [allGalleries, setAllGalleries] = useState([]);
  const [filteredGalleries, setFilteredGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([{ name: "All", count: 0 }]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hoveredImage, setHoveredImage] = useState(null);
  const postsPerPage = 6;

  // Function to remove file extension from image name
  const removeFileExtension = (filename) => {
    if (!filename) return "Gallery Image";
    return filename.replace(/\.[^/.]+$/, ""); // Remove everything after the last dot
  };

  // Fetch ALL galleries on component mount
  useEffect(() => {
    const fetchAllGalleries = async () => {
      try {
        setLoading(true);
        // Fetch all galleries
        const response = await axios.get(
          `${CMSAPI}/api/Gallery/get-gallerys?PageNo=1&PageSize=1000&domain=GYANYANTRAM`
        );

        if (response.data && response.data.data) {
          const transformedGalleries = response.data.data.map((gallery) => ({
            id: gallery.id,
            title: gallery.name || "Untitled Gallery",
            category: gallery.category || "Uncategorized",
            // Only use the photos array, not the main photo
            images: gallery.photos.map((photo) => ({
              url: photo.detail ? `${CMSAPI}${photo.detail}` : Policy,
              name: removeFileExtension(photo.name) // Remove file extension
            })),
            date: gallery.regDate
              ? new Date(gallery.regDate).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "June 9, 2025",
          }));

          setAllGalleries(transformedGalleries);

          // Calculate categories
          const counts = {};
          transformedGalleries.forEach((gallery) => {
            counts[gallery.category] = (counts[gallery.category] || 0) + 1;
          });

          // Define the desired category order
          const desiredOrder = [
            "Solution", 
            "Software", 
            "Utilities", 
            "IOT",
            "AI Solutions", 
            "Mobile Application", 
          ];
          
          // Create ordered categories array
          const orderedCategories = [];
          
          // Add categories in the desired order
          desiredOrder.forEach(category => {
            if (counts[category]) {
              orderedCategories.push({
                name: category,
                count: counts[category] || 0
              });
              delete counts[category];
            }
          });
          
          // Add any remaining categories that weren't in the desired order
          Object.keys(counts).forEach(category => {
            orderedCategories.push({
              name: category,
              count: counts[category] || 0
            });
          });
          
          // Add "All" category at the beginning
          const dynamicCategories = [
            { name: "All", count: transformedGalleries.length },
            ...orderedCategories
          ];

          setCategories(dynamicCategories);

          // Filter and paginate initially
          filterAndPaginateGalleries(transformedGalleries, "All", 1);
        }
      } catch (err) {
        console.error("Error fetching galleries:", err);
        setError("Failed to load galleries. Please try again later.");

        // Fallback data
        const fallbackGalleries = [
          {
            id: 1,
            title: "Demo Gallery",
            category: "Bin Monitoring",
            images: [
              { url: Policy, name: "Demo Image 1" },
              { url: Policy, name: "Demo Image 2" },
              { url: Policy, name: "Demo Image 3" }
            ],
            date: "June 9, 2025",
          },
        ];

        setAllGalleries(fallbackGalleries);
        setCategories([
          { name: "All", count: 1 },
          { name: "Bin Monitoring", count: 1 },
        ]);
        filterAndPaginateGalleries(fallbackGalleries, "All", 1);
      } finally {
        setLoading(false);
      }
    };

    fetchAllGalleries();
  }, []);

  // Filter and paginate galleries client-side
  const filterAndPaginateGalleries = (galleries, filter, page) => {
    // Filter by category
    const filtered =
      filter === "All"
        ? galleries
        : galleries.filter((g) => g.category === filter);

    // Calculate pagination
    const totalPages = Math.ceil(filtered.length / postsPerPage);
    setTotalPages(totalPages);

    // Get galleries for current page
    const startIndex = (page - 1) * postsPerPage;
    const paginatedGalleries = filtered.slice(
      startIndex,
      startIndex + postsPerPage
    );

    setFilteredGalleries(paginatedGalleries);
  };

  // Handle filter change
  const handleFilterChange = (category) => {
    setActiveFilter(category);
    setCurrentPage(1);
    filterAndPaginateGalleries(allGalleries, category, 1);
  };

  // Handle page change
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    filterAndPaginateGalleries(allGalleries, activeFilter, pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openGallery = (gallery) => {
    setSelectedGallery(gallery);
    setShowModal(true);
  };

  const closeGallery = () => {
    setShowModal(false);
    setSelectedGallery(null);
  };

  // if (loading && currentPage === 1) {
  //   return (
  //     <div className="flex justify-center items-center h-64">
  //       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00D084]"></div>
  //     </div>
  //   );
  // }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>

      <PageBanner
        title="Our Gallery"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Resources" },
          { label: "Gallery" },
        ]}
      />

      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="Gallery @ AJEEVI"
          shareUrl="https://gyanyantram.ajeevi.in/gallery"
        />
      </div>

      <div>
        <ScrollToTopButton />
      </div>

      {/* Gallery Modal */}
      {showModal && selectedGallery && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4">
          <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header with Close Button */}
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-xl font-bold">{selectedGallery.title}</h3>
                <span className="bg-[#00D084] text-white px-3 py-1 text-sm font-semibold rounded-full">
                  {selectedGallery.category}
                </span>
              </div>
              <button
                onClick={closeGallery}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Images Grid - 3 per row with scrolling */}
            <div className="p-4 overflow-y-auto flex-grow">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedGallery.images.map((image, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-100 rounded-lg overflow-hidden relative group"
                    onMouseEnter={() => setHoveredImage(idx)}
                    onMouseLeave={() => setHoveredImage(null)}
                  >
                    <img
                      src={image.url}
                      alt={`${selectedGallery.title} - ${image.name}`}
                      className="w-full h-64 object-cover hover:opacity-90 transition-opacity"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = Policy;
                      }}
                    />
                    {/* Image name overlay on hover */}
                    {hoveredImage === idx && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 transition-opacity">
                        <p className="text-white text-center text-sm font-medium">
                          {image.name}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-10 py-0 pb-5 mb-10 bg-white font-poppins">
        <h2 className="text-3xl sm:text-2xl font-medium text-[#00D084] mb-4 text-center">
          Our Gallery Collection
        </h2>
        <h3 className="text-xl md:text-3xl font-medium text-gray-900 mb-2 text-center">
          Visualizing Gyanyantram IoT
        </h3>
        <p className="text-black text-base sm:text-lg max-w-3xl mx-auto mb-10 text-center">
         Explore our collection of images showcasing innovative IoT devices, smart automation, real-time monitoring, and connected solutions that drive efficiency and intelligence across industries.
        </p>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Categories Sidebar */}
          <div className="w-full md:w-1/5">
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm sticky top-4">
              <h4 className="text-lg font-semibold mb-4 text-gray-800">
                Offerings
              </h4>
              <ul className="space-y-2">
                {categories.map((category, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handleFilterChange(category.name)}
                      className={`w-full text-left px-4 py-2 rounded-md transition-colors flex justify-between items-center ${
                        activeFilter === category.name
                          ? "bg-[#00D084] text-white"
                          : "text-black hover:bg-gray-100"
                      }`}
                    >
                      <span>{category.name}</span>
                      <span className="bg-white text-[#00D084] text-xs font-semibold px-2 py-1 rounded-full">
                        {category.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="w-full md:w-4/5">
            {filteredGalleries.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredGalleries.map((gallery) => (
                    <div key={gallery.id} className="group relative">
                      {/* Gallery Card */}
                      <div
                        className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition duration-300 cursor-pointer flex flex-col"
                        onClick={() => openGallery(gallery)}
                      >
                        {/* Main Image - Fixed height */}
                        <div className="relative h-48 flex-shrink-0">
                          {gallery.images.length > 0 ? (
                            <img
                              src={gallery.images[0].url}
                              alt={gallery.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = Policy;
                              }}
                            />
                          ) : (
                            <img
                              src={Policy}
                              alt="Default image"
                              className="w-full h-full object-cover"
                            />
                          )}

                          {/* Image Count Badge */}
                          {gallery.images.length > 1 && (
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-sm">
                              +{gallery.images.length - 1} more
                            </div>
                          )}
                        </div>

                        {/* Gallery Info - Flexible content area */}
                        <div className="p-4 flex flex-col flex-grow">
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="text-lg font-bold text-gray-900 line-clamp-2">
                              {gallery.title}
                            </h3>
                            <span className="bg-[#00D084] text-white px-1 py-1 text-sm font-semibold rounded-full whitespace-nowrap ml-2 flex-shrink-0">
                              {gallery.category}
                            </span>
                          </div>
                          {/* <p className="text-sm text-gray-500 mt-auto">{gallery.date}</p> */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-8">
                    <nav className="inline-flex rounded-md shadow">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-l-md border ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, index) => (
                        <button
                          key={index}
                          onClick={() => paginate(index + 1)}
                          className={`px-4 py-2 border-t border-b ${
                            currentPage === index + 1
                              ? "bg-[#00D084] text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-r-md border ${
                          currentPage === totalPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500">
                  No galleries found in this category.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Gallery;



// import { useState, useEffect } from "react";
// import axios from "axios";
// import { Link } from "react-router-dom";
// import PageBanner from "../PageBanner";
// import SocialMediaSidebar from "../SocialMediaSidebar";
// import SectionHeader from "../SectionHeader";
// import { CMSAPI } from "../../config";
// import ScrollToTopButton from "../ScrollToTopButton";

// import Policy from "../../assets/images/blog3.png";

// const Gallery = () => {
//   const [activeFilter, setActiveFilter] = useState("All");
//   const [galleries, setGalleries] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [categories, setCategories] = useState([{ name: "All", count: 0 }]);
//   const [selectedGallery, setSelectedGallery] = useState(null);
//   const [showModal, setShowModal] = useState(false);

//   useEffect(() => {
//     const fetchGalleries = async () => {
//       try {
//         const response = await axios.get(
//           `${CMSAPI}/api/Gallery/get-gallerys?PageNo=1&PageSize=10&domain=PARKING`
//         );

//         if (response.data && response.data.data) {
//           const transformedGalleries = response.data.data.map((gallery) => ({
//             id: gallery.id,
//             title: gallery.name || "Untitled Gallery",
//             category: gallery.category || "Uncategorized",
//             images: [
//               gallery.photo ? `${CMSAPI}${gallery.photo}` : Policy,
//               ...gallery.photos.map((photo) =>
//                 photo.detail ? `${CMSAPI}${photo.detail}` : Policy
//               ),
//             ],
//             date: gallery.regDate
//               ? new Date(gallery.regDate).toLocaleDateString("en-US", {
//                   year: "numeric",
//                   month: "long",
//                   day: "numeric",
//                 })
//               : "June 9, 2025",
//           }));

//           setGalleries(transformedGalleries);

//           // Generate categories
//           const categoryCounts = transformedGalleries.reduce((acc, gallery) => {
//             acc[gallery.category] = (acc[gallery.category] || 0) + 1;
//             return acc;
//           }, {});

//           setCategories([
//             { name: "All", count: transformedGalleries.length },
//             ...Object.entries(categoryCounts).map(([name, count]) => ({
//               name,
//               count,
//             })),
//           ]);
//         }
//       } catch (err) {
//         console.error("Error fetching galleries:", err);
//         setError("Failed to load galleries. Please try again later.");

//         // Fallback data
//         setGalleries([
//           {
//             id: 1,
//             title: "Demo Gallery",
//             category: "Bin Monitoring",
//             images: [Policy, Policy, Policy, Policy, Policy, Policy],
//             date: "June 9, 2025",
//           },
//         ]);

//         setCategories([
//           { name: "All", count: 1 },
//           { name: "Bin Monitoring", count: 1 },
//         ]);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchGalleries();
//   }, []);

//   const openGallery = (gallery) => {
//     setSelectedGallery(gallery);
//     setShowModal(true);
//   };

//   const closeGallery = () => {
//     setShowModal(false);
//     setSelectedGallery(null);
//   };

//   const filteredGalleries =
//     activeFilter === "All"
//       ? galleries
//       : galleries.filter((gallery) => gallery.category === activeFilter);

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-64">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00D084]"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return <div className="text-center py-10 text-red-500">{error}</div>;
//   }

//   return (
//     <>
//       <div>
//         <SocialMediaSidebar />
//       </div>

//       <PageBanner
//         title="Our Gallery"
//         breadcrumb={[
//           { label: "Home", href: "/" },
//           { label: "Resources" },
//           { label: "Gallery" },
//         ]}
//       />

//       <div className="max-w-6xl mx-auto p-4">
//         <SectionHeader
//           title="Gallery @ AJEEVI"
//           shareUrl="https://swmecom.ajeevi.in/gallery"
//         />
//       </div>

//       <div>
//         <ScrollToTopButton />
//       </div>

//       {/* Gallery Modal */}
//       {showModal && selectedGallery && (
//         <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4">
//           <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
//             {/* Modal Header with Close Button */}
//             <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
//               <div>
//                 <h3 className="text-xl font-bold">{selectedGallery.title}</h3>
//                 <span className="bg-[#00D084] text-white px-3 py-1 text-sm font-semibold rounded-full">
//                   {selectedGallery.category}
//                 </span>
//               </div>
//               <button
//                 onClick={closeGallery}
//                 className="text-gray-500 hover:text-gray-700 focus:outline-none"
//               >
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   className="h-8 w-8"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M6 18L18 6M6 6l12 12"
//                   />
//                 </svg>
//               </button>
//             </div>

//             {/* Images Grid - 3 per row with scrolling */}
//             <div className="p-4 overflow-y-auto flex-grow">
//               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
//                 {selectedGallery.images.map((image, idx) => (
//                   <div
//                     key={idx}
//                     className="bg-gray-100 rounded-lg overflow-hidden"
//                   >
//                     <img
//                       src={image}
//                       alt={`${selectedGallery.title} - Image ${idx + 1}`}
//                       className="w-full h-64 object-cover hover:opacity-90 transition-opacity"
//                       onError={(e) => {
//                         e.target.onerror = null;
//                         e.target.src = Policy;
//                       }}
//                     />
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Main Content */}
//       <div className="w-full px-4 sm:px-6 md:px-10 lg:px-10 py-0 pb-5 mb-10 bg-white font-poppins">
//         <h2 className="text-3xl sm:text-2xl font-medium text-[#00D084] mb-4 text-center">
//           Our Gallery Collection
//         </h2>
//         <h3 className="text-xl md:text-3xl font-medium text-gray-900 mb-2 text-center">
//           Visualizing GyanYantram
//         </h3>
//         <p className="text-black text-base sm:text-lg max-w-3xl mx-auto mb-10 text-center">
//            Explore our gallery of images highlighting digital initiatives, on-ground smart governance implementations, and cutting-edge solutions developed under the GyanYantram platform.
//         </p>

//         <div className="flex flex-col md:flex-row gap-8">
//           {/* Categories Sidebar */}
//           <div className="w-full md:w-1/5">
//             <div className="bg-gray-50 p-4 rounded-lg shadow-sm sticky top-4">
//               <h4 className="text-lg font-semibold mb-4 text-gray-800">
//                 Categories
//               </h4>
//               <ul className="space-y-2">
//                 {categories.map((category, index) => (
//                   <li key={index}>
//                     <button
//                       onClick={() => setActiveFilter(category.name)}
//                       className={`w-full text-left px-4 py-2 rounded-md transition-colors flex justify-between items-center ${
//                         activeFilter === category.name
//                           ? "bg-[#00D084] text-white"
//                           : "text-black hover:bg-gray-100"
//                       }`}
//                     >
//                       <span>{category.name}</span>
//                       <span className="bg-white text-[#00D084] text-xs font-semibold px-2 py-1 rounded-full">
//                         {category.count}
//                       </span>
//                     </button>
//                   </li>
//                 ))}
//               </ul>
//             </div>
//           </div>

//           {/* Gallery Grid */}
//           <div className="w-full md:w-4/5">
//             {filteredGalleries.length > 0 ? (
//               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//                 {filteredGalleries.map((gallery) => (
//                   <div key={gallery.id} className="group relative">
//                     {/* Gallery Card */}
//                     <div
//                       className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition duration-300 cursor-pointer"
//                       onClick={() => openGallery(gallery)}
//                     >
//                       {/* Main Image */}
//                       <div className="relative aspect-square">
//                         <img
//                           src={gallery.images[0]}
//                           alt={gallery.title}
//                           className="w-full h-full object-cover"
//                           onError={(e) => {
//                             e.target.onerror = null;
//                             e.target.src = Policy;
//                           }}
//                         />

//                         {/* Image Count Badge */}
//                         {gallery.images.length > 1 && (
//                           <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-sm">
//                             +{gallery.images.length - 1} more
//                           </div>
//                         )}
//                       </div>

//                       {/* Gallery Info */}
//                       <div className="p-4">
//                         <div className="flex justify-between items-start">
//                           <h3 className="text-lg font-bold text-gray-900 mb-1">
//                             {gallery.title}
//                           </h3>
//                           <span className="bg-[#00D084] text-white px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ml-2">
//                             {gallery.category}
//                           </span>
//                         </div>
//                         <p className="text-sm text-gray-500">{gallery.date}</p>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             ) : (
//               <div className="text-center py-10">
//                 <p className="text-gray-500">
//                   No galleries found in this category.
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default Gallery;
